import { useState } from 'react'
import { useCustomers, useProjects, useSaveDevice } from '../api/queries'
import type { DeviceDetail } from '../api/types'
import { serviceIntervals, suggestedServiceInterval } from '../api/types'
import { projectOptionLabel } from '../lib/projectLabel'
import { DeviceTypeFields } from './DeviceTypeFields'
import { Card } from './ui'

/**
 * Opret eller ret en enhed.
 *
 * Serviceaftalen sættes her: interval i måneder, og hvornår der næste gang skal
 * ud. Uden interval er enheden bare registreret - den dukker ikke op i
 * serviceoverblikket (docs/DECISIONS.md beslutning 024).
 */
export function DeviceForm({
  device,
  projectId,
  customerId,
  onClose,
  onSaved
}: {
  device: DeviceDetail | null
  /** Forudfyldt sag, når enheden oprettes fra en sag der afsluttes. */
  projectId?: number
  /** Forudfyldt kunde, f.eks. sagens kunde. */
  customerId?: number
  onClose: () => void
  onSaved: (device: DeviceDetail) => void
}) {
  const save = useSaveDevice(device?.device.id)
  const { data: customers } = useCustomers()
  const { data: projects } = useProjects({ pageSize: 200 })

  const [form, setForm] = useState({
    name: device?.device.name ?? '',
    deviceType: device?.device.deviceType ?? '',
    serialNumber: device?.device.serialNumber ?? '',
    manufacturer: device?.manufacturer ?? '',
    model: device?.model ?? '',
    customerId: String(device?.device.customerId ?? customerId ?? ''),
    installedProjectId: String(device?.installedProjectId ?? projectId ?? ''),
    location: device?.device.location ?? '',
    // Enhedens egen adresse, naar den ikke staar paa kundens (beslutning 045).
    siteAddress: device?.workSite?.address ?? '',
    sitePostalCode: device?.workSite?.postalCode ?? '',
    siteCity: device?.workSite?.city ?? '',
    notes: device?.notes ?? '',
    installedOn: toDateInput(device?.installedOn),
    serviceIntervalMonths: device?.device.serviceIntervalMonths
      ? String(device.device.serviceIntervalMonths)
      : '',
    nextServiceDate: toDateInput(device?.device.nextServiceDate),
    isActive: device?.device.isActive ?? true
  })
  const [error, setError] = useState<string | null>(null)

  function update<K extends keyof typeof form>(field: K, value: (typeof form)[K]) {
    setForm((current) => {
      const next = { ...current, [field]: value }

      // Skifter man enhedstype til noget med et lovbestemt interval, foreslås det -
      // men kun når feltet står tomt, så et bevidst valg aldrig bliver overskrevet.
      if (field === 'deviceType' && !current.serviceIntervalMonths) {
        const rule = suggestedServiceInterval(String(value))
        if (rule) next.serviceIntervalMonths = String(rule.months)
      }

      return next
    })
  }

  const intervalRule = suggestedServiceInterval(form.deviceType)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)

    if (!form.customerId) {
      setError('Vælg hvilken kunde enheden står hos.')
      return
    }

    try {
      const saved = await save.mutateAsync({
        name: form.name,
        deviceType: form.deviceType || null,
        serialNumber: form.serialNumber || null,
        manufacturer: form.manufacturer || null,
        model: form.model || null,
        customerId: Number(form.customerId),
        installedProjectId: form.installedProjectId ? Number(form.installedProjectId) : null,
        location: form.location || null,
        siteAddress: form.siteAddress || null,
        sitePostalCode: form.sitePostalCode || null,
        siteCity: form.siteCity || null,
        notes: form.notes || null,
        installedOn: form.installedOn ? new Date(form.installedOn).toISOString() : null,
        serviceIntervalMonths: form.serviceIntervalMonths ? Number(form.serviceIntervalMonths) : null,
        nextServiceDate: form.nextServiceDate ? new Date(form.nextServiceDate).toISOString() : null,
        isActive: form.isActive
      })
      onSaved(saved)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enheden kunne ikke gemmes.')
    }
  }

  return (
    <Card title={device ? `Ret ${device.device.name}` : 'Ny enhed'}>
      <form onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Navn *</span>
            <input
              required
              placeholder="f.eks. Batteribank A"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
            />
          </label>

          {/* Type og model fra den faste liste (beslutning 105). */}
          <DeviceTypeFields
            deviceType={form.deviceType}
            model={form.model}
            manufacturer={form.manufacturer}
            onChange={(next) => {
              if (next.deviceType !== form.deviceType) update('deviceType', next.deviceType)
              setForm((current) => ({ ...current, model: next.model, manufacturer: next.manufacturer }))
            }}
          />

          <label className="field">
            <span className="field__label">Kunde *</span>
            <select
              required
              value={form.customerId}
              onChange={(e) => update('customerId', e.target.value)}
            >
              <option value="">Vælg kunde</option>
              {customers?.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Placering i bygningen</span>
            <input
              placeholder="f.eks. Teknikrum, kælder"
              value={form.location}
              onChange={(e) => update('location', e.target.value)}
            />
            <span className="tiny muted">Hvor enheden står, når man er fremme - ikke adressen.</span>
          </label>

          {/* Adressen er noget andet end placeringen: den ene fortæller hvor man
              skal køre hen, den anden hvor man skal lede, når man er der. */}
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <span className="field__label">Adresse, hvis enheden ikke står på kundens</span>
            <div className="site-fields">
              <input
                placeholder="Vej og nr. - tom hvis det er på kundens adresse"
                value={form.siteAddress}
                onChange={(e) => update('siteAddress', e.target.value)}
              />
              <input
                placeholder="Postnr."
                style={{ maxWidth: 110 }}
                value={form.sitePostalCode}
                onChange={(e) => update('sitePostalCode', e.target.value)}
              />
              <input
                placeholder="By"
                value={form.siteCity}
                onChange={(e) => update('siteCity', e.target.value)}
              />
            </div>
            <span className="tiny muted">
              Udfyld kun, når enheden står et andet sted end kundens egen adresse - f.eks. en
              ejendom på en anden matrikel. Ellers bruges kundens adresse på kortet.
            </span>
          </div>

          <label className="field">
            <span className="field__label">Serienummer</span>
            <input value={form.serialNumber} onChange={(e) => update('serialNumber', e.target.value)} />
          </label>

          <label className="field">
            <span className="field__label">Fabrikat</span>
            <input value={form.manufacturer} onChange={(e) => update('manufacturer', e.target.value)} />
          </label>

          <label className="field">
            <span className="field__label">Installeret</span>
            <input
              type="date"
              value={form.installedOn}
              onChange={(e) => update('installedOn', e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field__label">Service</span>
            <select
              value={form.serviceIntervalMonths}
              onChange={(e) => update('serviceIntervalMonths', e.target.value)}
            >
              <option value="">Ingen serviceaftale</option>
              {serviceIntervals.map((interval) => (
                <option key={interval.months} value={interval.months}>
                  {interval.label}
                </option>
              ))}
            </select>
          </label>

          {intervalRule && (
            <div className="field">
              <span className="field__label">Bemærk</span>
              <span className="tiny muted">{intervalRule.why}</span>
            </div>
          )}

          {form.serviceIntervalMonths && (
            <label className="field">
              <span className="field__label">Næste service</span>
              <input
                type="date"
                value={form.nextServiceDate}
                onChange={(e) => update('nextServiceDate', e.target.value)}
              />
              <span className="tiny muted">Tom = regnes ud fra intervallet</span>
            </label>
          )}

          <label className="field">
            <span className="field__label">Installeret under sag</span>
            <select
              value={form.installedProjectId}
              onChange={(e) => update('installedProjectId', e.target.value)}
            >
              <option value="">Ingen sag</option>
              {projects?.items.map((project) => (
                <option key={project.id} value={project.id}>
                  {projectOptionLabel(project)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="field">
          <span className="field__label">Noter</span>
          <textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} />
        </label>

        {device && (
          <label className="row small" style={{ gap: 6, marginBottom: 12 }}>
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={!form.isActive}
              onChange={(event) => update('isActive', !event.target.checked)}
            />
            Enheden er udtjent (skjules i listen, men historikken bevares)
          </label>
        )}

        {error && <div className="error-box" style={{ marginBottom: 12 }}>{error}</div>}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Gemmer ...' : device ? 'Gem' : 'Opret enhed'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

/** ISO-dato til <input type="date">. */
function toDateInput(value?: string | null) {
  return value ? value.slice(0, 10) : ''
}
